import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { layoutStyles, isMobile } from '../styles/sharedStyles';
import { getAdminUser, logout } from '../utils/auth';
import Button from './Button';

const Layout = ({ children, title = 'Admin Panel' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const adminUser = getAdminUser();

  const [hoveredMenuItem, setHoveredMenuItem] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isMobileView, setIsMobileView] = useState(false);

  // Check if mobile view on mount and window resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobileView(isMobile());
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const menuItems = [
    { path: '/dashboard', label: 'Dashboard', icon: '📊' },
    { path: '/members', label: 'Members', icon: '👥' },
    { path: '/admins', label: 'Admins', icon: '🔐' },
    { path: '/profile', label: 'Profile', icon: '👤' },
  ];

  const isActiveRoute = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const getMenuItemStyle = (path) => {
    const isActive = isActiveRoute(path);
    const isHovered = hoveredMenuItem === path;

    return {
      ...layoutStyles.menuItem,
      ...(isHovered && !isActive ? layoutStyles.menuItemHover : {}),
      ...(isActive ? layoutStyles.menuItemActive : {}),
    };
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const handleMenuItemClick = (path) => {
    navigate(path);
    if (isMobileView) {
      setIsSidebarOpen(false);
    }
  };

  const getSidebarStyle = () => {
    if (!isMobileView) {
      return layoutStyles.sidebar;
    }
    return {
      ...layoutStyles.sidebar,
      ...(isSidebarOpen ? layoutStyles.sidebarOpen : layoutStyles.sidebarMobile),
    };
  };

  const getMainContentStyle = () => {
    if (isMobileView) {
      return {
        ...layoutStyles.mainContent,
        ...layoutStyles.mainContentMobile,
      };
    }
    return layoutStyles.mainContent;
  };

  return (
    <div style={layoutStyles.container}>
      {/* Overlay for mobile sidebar */}
      {isMobileView && isSidebarOpen && (
        <div
          style={{
            ...layoutStyles.overlay,
            ...layoutStyles.overlayVisible,
          }}
          onClick={toggleSidebar}
        />
      )}

      {/* Sidebar */}
      <div style={getSidebarStyle()}>
        {/* Sidebar Header */}
        <div style={layoutStyles.sidebarHeader}>
          <span>NAUS Admin</span>
          {isMobileView && (
            <button
              style={layoutStyles.closeButton}
              onClick={toggleSidebar}
              aria-label="Close menu"
            >
              ×
            </button>
          )}
        </div>

        {/* Menu Items */}
        <ul style={layoutStyles.sidebarMenu}>
          {menuItems.map((item) => (
            <li
              key={item.path}
              style={getMenuItemStyle(item.path)}
              onClick={() => handleMenuItemClick(item.path)}
              onMouseEnter={() => setHoveredMenuItem(item.path)}
              onMouseLeave={() => setHoveredMenuItem(null)}
            >
              <span>{item.icon}</span>
              <span>{item.label}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Main Content Area */}
      <div style={getMainContentStyle()}>
        {/* Header */}
        <div style={layoutStyles.header} className="layout-header">
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <button
              style={layoutStyles.hamburgerButton}
              className="hamburger-button"
              onClick={toggleSidebar}
              aria-label="Toggle menu"
            >
              ☰
            </button>
            <h1 style={layoutStyles.headerTitle}>{title}</h1>
          </div>
          <div style={layoutStyles.headerActions}>
            <span
              className="welcome-text"
              style={{
                fontSize: '14px',
                color: '#6c757d',
              }}
            >
              Welcome, <strong>{adminUser?.name || 'Admin'}</strong>
            </span>
            <Button variant="danger" size="small" onClick={handleLogout}>
              Logout
            </Button>
          </div>
        </div>

        {/* Content */}
        <div style={layoutStyles.content} className="layout-content">{children}</div>
      </div>
    </div>
  );
};

export default Layout;
